import { Link } from "react-router-dom";
import { FOOTER_LINKS, SITE } from "@/lib/constants";
import { Mail, MessageCircle, Instagram, Timer, Tag, ShoppingBasket, Wallet } from "lucide-react";

const FEATURES = [
  { icon: Timer, title: "Delivery in Minutes", text: "Right to your doorstep" },
  { icon: Tag, title: "Best Prices & Offers", text: "Great value every day" },
  { icon: ShoppingBasket, title: "Wide Assortment", text: "Groceries, dairy & more" },
  { icon: Wallet, title: "Cash on Delivery", text: "Pay when it arrives" },
];

export function Footer() {
  const year = new Date().getFullYear();
  const waHref = `https://wa.me/${SITE.whatsapp}`;
  const left = FOOTER_LINKS.slice(0, 5);
  const right = FOOTER_LINKS.slice(5);

  return (
    <footer className="mt-24 bg-ink text-cream">
      <div className="border-b border-white/10">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-6 px-4 py-10 sm:px-6 lg:grid-cols-4 lg:px-8" data-testid="footer-features">
          {FEATURES.map((f) => (
            <div key={f.title} className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand text-ink">
                <f.icon className="h-5 w-5" />
              </span>
              <div>
                <p className="font-semibold text-cream">{f.title}</p>
                <p className="text-sm text-cream/60">{f.text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-4 py-16 sm:grid-cols-2 sm:px-6 lg:grid-cols-5 lg:px-8">
        <div className="md:col-span-1">
          <div className="inline-flex overflow-hidden rounded-xl">
            <img src="/nicee-logo.jpg" alt="Nicee" className="h-12 w-12 object-cover" />
          </div>
          <p className="mt-4 max-w-xs text-sm text-cream/70">{SITE.tagline}. Fresh essentials to your door.</p>
          <div className="mt-5 flex gap-3">
            <a href={waHref} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 transition-colors hover:bg-brand hover:text-ink" data-testid="footer-whatsapp">
              <MessageCircle className="h-4 w-4" />
            </a>
            <a href={SITE.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 transition-colors hover:bg-brand hover:text-ink" data-testid="footer-instagram">
              <Instagram className="h-4 w-4" />
            </a>
          </div>
        </div>

        <div>
          <h4 className="text-xs font-semibold uppercase tracking-[0.2em] text-cream/60">Explore</h4>
          <ul className="mt-4 space-y-3 text-sm">
            {left.map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="text-cream/80 transition-colors hover:text-brand">
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="text-xs font-semibold uppercase tracking-[0.2em] text-cream/60">Company</h4>
          <ul className="mt-4 space-y-3 text-sm">
            {right.map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="text-cream/80 transition-colors hover:text-brand">
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div data-testid="footer-for-sellers">
          <h4 className="text-xs font-semibold uppercase tracking-[0.2em] text-cream/60">For Sellers</h4>
          <ul className="mt-4 space-y-3 text-sm">
            <li>
              <Link to="/seller/login" className="text-cream/80 transition-colors hover:text-brand" data-testid="footer-seller-login">Seller Login</Link>
            </li>
            <li>
              <Link to="/seller/register" className="text-cream/80 transition-colors hover:text-brand" data-testid="footer-become-seller">Become a Seller</Link>
            </li>
          </ul>
        </div>

        <div>
          <h4 className="text-xs font-semibold uppercase tracking-[0.2em] text-cream/60">Get in touch</h4>
          <ul className="mt-4 space-y-3 text-sm">
            <li>
              <a href={`mailto:${SITE.email}`} className="flex items-center gap-2 text-cream/80 transition-colors hover:text-brand">
                <Mail className="h-4 w-4" /> {SITE.email}
              </a>
            </li>
            <li>
              <a href={waHref} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-cream/80 transition-colors hover:text-brand">
                <MessageCircle className="h-4 w-4" /> {SITE.whatsappDisplay}
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-2 px-4 py-6 text-center text-xs text-cream/60 sm:flex-row sm:px-6 lg:px-8">
          <span>© {year} {SITE.name}. All rights reserved.</span>
          <Link to="/admin/login" className="text-cream/60 transition-colors hover:text-brand" data-testid="footer-admin-login">Admin Login</Link>
        </div>
      </div>
    </footer>
  );
}
